// Validation Configuration Form Component
// Simple form for configuring field validation rules

import React, { useState, useEffect } from "react";
import { createLogger } from "../../utils/logger";

const logger = createLogger("src/components/Validations/ValidationConfigForm.jsx");

const ValidationConfigForm = ({ 
  validation = null, 
  formFields = [], 
  onSave, 
  onCancel,
  mode = "add"
}) => {
  logger.entry("ValidationConfigForm", { mode, hasValidation: !!validation });

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "visibility",
    conditionLogic: "AND",
    conditions: [{
      field: "",
      operator: "equals",
      value: ""
    }],
    thenAction: "show",
    elseAction: "hide"
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (validation && mode === "edit") {
      logger.info("useEffect", "Loading validation for edit", { validationId: validation.id });
      setFormData({
        name: validation.name || "",
        description: validation.description || "",
        type: validation.type || "visibility",
        conditionLogic: validation.conditionLogic || "AND",
        conditions: validation.conditions || [{
          field: "",
          operator: "equals",
          value: ""
        }],
        thenAction: validation.thenAction || "show",
        elseAction: validation.elseAction || "hide"
      });
    }
  }, [validation, mode]);

  const operators = [
    { value: "equals", label: "is equal to" },
    { value: "notEquals", label: "is not equal to" },
    { value: "greaterThan", label: "is greater than" },
    { value: "lessThan", label: "is less than" },
    { value: "greaterOrEqual", label: "is greater than or equal to" },
    { value: "lessOrEqual", label: "is less than or equal to" },
    { value: "contains", label: "contains" },
    { value: "notContains", label: "does not contain" },
    { value: "isEmpty", label: "is empty" },
    { value: "isNotEmpty", label: "is not empty" }
  ];

  const visibilityActions = [
    { value: "show", label: "Show field" },
    { value: "hide", label: "Hide field" }
  ];

  const requiredActions = [
    { value: "required", label: "Make required" },
    { value: "optional", label: "Make optional" }
  ];

  const handleChange = (field, value) => {
    logger.debug("handleChange", "Field value changed", { field, value });
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleConditionChange = (index, field, value) => {
    logger.debug("handleConditionChange", "Condition updated", { index, field, value });
    const newConditions = [...formData.conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setFormData(prev => ({ ...prev, conditions: newConditions }));
  };

  const addCondition = () => {
    logger.info("addCondition", "Adding new condition");
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: "", operator: "equals", value: "" }]
    }));
  };

  const removeCondition = (index) => {
    logger.info("removeCondition", "Removing condition", { index });
    if (formData.conditions.length > 1) {
      setFormData(prev => ({
        ...prev,
        conditions: prev.conditions.filter((_, i) => i !== index)
      }));
    }
  };

  const validate = () => {
    logger.entry("validate", "Validating form");
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Validation name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    formData.conditions.forEach((condition, index) => {
      if (!condition.field) {
        newErrors[`condition_${index}_field`] = "Field is required";
      }
      if (!condition.operator) {
        newErrors[`condition_${index}_operator`] = "Operator is required";
      }
      if (condition.operator !== "isEmpty" && condition.operator !== "isNotEmpty" && !condition.value) {
        newErrors[`condition_${index}_value`] = "Value is required";
      }
    });

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    logger.exit("validate", { isValid, errorCount: Object.keys(newErrors).length });
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    logger.entry("handleSubmit", "Form submitted");

    if (!validate()) {
      logger.warn("handleSubmit", "Validation failed");
      return;
    }

    const validationData = {
      ...formData,
      id: validation?.id || `val_${Date.now()}`,
      version: validation?.version || "1.0.0",
      createdAt: validation?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    logger.info("handleSubmit", "Saving validation", { validationId: validationData.id });
    onSave(validationData);
  };

  const styles = {
    container: {
      padding: "20px",
      maxWidth: "600px"
    },
    formGroup: {
      marginBottom: "16px"
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      marginBottom: "6px",
      color: "#374151"
    },
    input: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #D1D5DB",
      borderRadius: "6px",
      fontSize: "14px"
    },
    select: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #D1D5DB",
      borderRadius: "6px",
      fontSize: "14px",
      backgroundColor: "white"
    },
    textarea: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #D1D5DB",
      borderRadius: "6px",
      fontSize: "14px",
      minHeight: "60px",
      fontFamily: "inherit",
      resize: "vertical"
    },
    error: {
      fontSize: "12px",
      color: "#DC2626",
      marginTop: "4px"
    },
    conditionRow: {
      display: "flex",
      gap: "8px",
      alignItems: "flex-start",
      marginBottom: "12px"
    },
    conditionField: {
      flex: "1"
    },
    removeButton: {
      padding: "8px 12px",
      background: "#EF4444",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "14px"
    },
    addButton: {
      padding: "8px 16px",
      background: "#10B981",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "14px",
      marginBottom: "16px"
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      marginTop: "24px"
    },
    cancelButton: {
      padding: "10px 20px",
      background: "#6B7280",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "14px"
    },
    saveButton: {
      padding: "10px 20px",
      background: "#3B82F6",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500"
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.container}>
      <div style={styles.formGroup}>
        <label style={styles.label}>Validation Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="e.g., Show Marital Status for Adults"
          style={styles.input}
        />
        {errors.name && <div style={styles.error}>{errors.name}</div>}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Description *</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Describe what this validation does..."
          style={styles.textarea}
        />
        {errors.description && <div style={styles.error}>{errors.description}</div>}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Validation Type</label>
        <select
          value={formData.type}
          onChange={(e) => handleChange("type", e.target.value)}
          style={styles.select}
        >
          <option value="visibility">Visibility (Show/Hide)</option>
          <option value="required">Required (Make field required/optional)</option>
        </select>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Conditions (IF)</label>
        
        {formData.conditions.length > 1 && (
          <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ fontSize: "13px", color: "#6B7280" }}>Condition Logic:</label>
            <select
              value={formData.conditionLogic}
              onChange={(e) => handleChange("conditionLogic", e.target.value)}
              style={{
                padding: "4px 8px",
                fontSize: "13px",
                border: "1px solid #D1D5DB",
                borderRadius: "4px",
                backgroundColor: "white"
              }}
            >
              <option value="AND">All conditions must be true (AND)</option>
              <option value="OR">Any condition can be true (OR)</option>
            </select>
          </div>
        )}
        
        {formData.conditions.map((condition, index) => (
          <div key={index}>
            <div style={styles.conditionRow}>
              <div style={styles.conditionField}>
                <select
                  value={condition.field}
                  onChange={(e) => handleConditionChange(index, "field", e.target.value)}
                  style={styles.select}
                >
                  <option value="">Select field...</option>
                  {formFields.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.label || field.id}
                    </option>
                  ))}
                </select>
                {errors[`condition_${index}_field`] && (
                  <div style={styles.error}>{errors[`condition_${index}_field`]}</div>
                )}
              </div>

              <div style={styles.conditionField}>
                <select
                  value={condition.operator}
                  onChange={(e) => handleConditionChange(index, "operator", e.target.value)}
                  style={styles.select}
                >
                  {operators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
                {errors[`condition_${index}_operator`] && (
                  <div style={styles.error}>{errors[`condition_${index}_operator`]}</div>
                )}
              </div>

              {condition.operator !== "isEmpty" && condition.operator !== "isNotEmpty" && (
                <div style={styles.conditionField}>
                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) => handleConditionChange(index, "value", e.target.value)}
                    placeholder="Value..."
                    style={styles.input}
                  />
                  {errors[`condition_${index}_value`] && (
                    <div style={styles.error}>{errors[`condition_${index}_value`]}</div>
                  )}
                </div>
              )}

              {formData.conditions.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCondition(index)}
                  style={styles.removeButton}
                >
                  ✕
                </button>
              )}
            </div>
            {index < formData.conditions.length - 1 && (
              <div style={{ textAlign: "center", margin: "8px 0", fontWeight: "500", color: "#3B82F6" }}>
                {formData.conditionLogic}
              </div>
            )}
          </div>
        ))}
        <button type="button" onClick={addCondition} style={styles.addButton}>
          + Add Another Condition
        </button>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>THEN (When conditions are met)</label>
        <select
          value={formData.thenAction}
          onChange={(e) => handleChange("thenAction", e.target.value)}
          style={styles.select}
        >
          {formData.type === "visibility"
            ? visibilityActions.map((action) => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))
            : requiredActions.map((action) => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))}
        </select>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>ELSE (When conditions are not met)</label>
        <select
          value={formData.elseAction}
          onChange={(e) => handleChange("elseAction", e.target.value)}
          style={styles.select}
        >
          {formData.type === "visibility"
            ? visibilityActions.map((action) => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))
            : requiredActions.map((action) => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))}
        </select>
      </div>

      <div style={styles.buttonGroup}>
        <button type="button" onClick={onCancel} style={styles.cancelButton}>
          Cancel
        </button>
        <button type="submit" style={styles.saveButton}>
          {mode === "edit" ? "Update Validation" : "Save Validation"}
        </button>
      </div>
    </form>
  );
};

export default ValidationConfigForm;

















